import { Link, useNavigate, useParams } from 'react-router-dom';
import GoodReviewIllustration from '@app/assets/illustrations/good-review.svg?react';
import { useState, useEffect } from 'react';
import YelpIcon from '@app/assets/icons/integrations/yelp.svg?react';
import GoogleIcon from '@app/assets/icons/Google.svg?react';
import { getCampaign, getCampaignPageDesigns } from '@app/services/campaigns';
import { CampaignPageDesignInterface } from '@app/models/campaigns';
import { getYelpCreateReviewUrl } from '@app/services/yelp';
import { getIntegration } from '@app/services/integrations';
import { IntegrationType } from '@app/utils/consts';

interface ReviewData {
  review: string;
  rating: number;
  yelpUrl?: string | null;
  googleUrl?: string | null;
  businessName?: string;
  businessAddress?: string;
  thumbnails: string[];
  timestamp: string;
  usedPlatform?: 'yelp' | 'google' | null;
}

export const LeaveReviewGood = () => {
  const navigate = useNavigate();
  const params = useParams();
  const requestId = params.requestId; // Gets the requestId from /leave-review/:requestId/thank-you

  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  
  // State for dynamic design
  const [positiveWindowDesign, setPositiveWindowDesign] = useState<CampaignPageDesignInterface | null>(null);
  const [isLoadingDesign, setIsLoadingDesign] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  
  // State for platform URLs
  const [yelpUrl, setYelpUrl] = useState<string | null>(null);
  const [googleUrl, setGoogleUrl] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [businessData, setBusinessData] = useState<{name: string, address: string} | null>(null);
  useEffect(() => {
    // Get review data from localStorage
    const storedData = localStorage.getItem('reviewData');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        setReviewData(data);
      } catch (error) {
        console.error('Error parsing review data:', error);
      }
    }   


    // Cleanup function to remove review data when component unmounts
    return () => {
      localStorage.removeItem('reviewData');
    };
  }, []);

  // Fetch campaign data to get businessId
  useEffect(() => {
    const fetchCampaignData = async () => {
      if (!requestId || !accessToken) return;
      
      try {
        const response = await getCampaign(requestId);
        
        if (response?.data) {
          setBusinessId(response.data.businessId);
          
          // Set business data if available
          if (response.data.business) {
            setBusinessData({
              name: response.data.business.name,
              address: response.data.business.address
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch campaign data:', error);
      }
    };

    fetchCampaignData();
  }, [requestId, accessToken]);

  // Extract access token from URL
  useEffect(() => {
    let token = null;
    
    // First, try standard query parameters
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get('t');
    
    // If not found, check if parameters are in the pathname (after requestId)
    if (!token) {
      const pathParts = window.location.pathname.split('/');
      const requestIdPart = pathParts[pathParts.length - 1];
      
      if (requestIdPart && requestIdPart.includes('&')) {
        const paramsString = requestIdPart.split('&').slice(1).join('&');
        const pathParams = new URLSearchParams(paramsString);
        token = pathParams.get('t');
      }
    }

    if (token) {
      setAccessToken(token);
    } 
  }, []);

  // Fetch positive window design
  useEffect(() => {
    const fetchPositiveWindowDesign = async () => {
      if (!requestId || !accessToken || !businessId) {
        return;
      }
      
      setIsLoadingDesign(true);
      try {
        const response = await getCampaignPageDesigns(
          requestId,
          accessToken,
          businessId
        );
        
        if (response.data.total > 0) {
          // Find positive window design
          const positiveDesign = response.data.pageDesigns.find(design => design.type === 'positiveWindowDesign');
          if (positiveDesign) {
            setPositiveWindowDesign(positiveDesign);
          }
        }
      } catch (error) {
        console.error('Failed to fetch positive window design:', error);
      } finally {
        setIsLoadingDesign(false);
      }
    };

    fetchPositiveWindowDesign();
  }, [requestId, accessToken, businessId]);

  // Fetch Yelp URL
  useEffect(() => {
    const checkYelpUrl = async () => {
      if (!requestId || !accessToken || !businessId) return;
      try {
        const response = await getYelpCreateReviewUrl(accessToken, businessId);
        if (response.data?.url) {
          setYelpUrl(response.data.url);
        }
      } catch (e) {
        setYelpUrl(null);
      }
    };
    checkYelpUrl();
  }, [requestId, accessToken, businessId]);

  // Fetch Google URL
  useEffect(() => {
    const checkGoogleUrl = async () => {
      if (!requestId || !accessToken || !businessId) return;
      try {
        const response = await getIntegration(IntegrationType.GOOGLE, accessToken, businessId);
        if (response.data?.integration?.connectionId) {
          setGoogleUrl(response.data.integration.connectionId);
        }
      } catch (e) {
        setGoogleUrl(null);
      }
    };
    checkGoogleUrl();
  }, [requestId, accessToken, businessId]);


  const handlePlatformClick = (platform: 'yelp' | 'google') => {
    const url = platform === 'yelp' ? yelpUrl : googleUrl;
    if (url) {
      // Open platform review page
      window.open(url, '_blank', 'noopener,noreferrer');
      
      // Navigate to thank you page with access token (no page refresh)
      const thankYouUrl = `/leave-review/${requestId}/thank-you?t=${accessToken}`;
      navigate(thankYouUrl);
    }
  };



  // Helper function to get button color from design
  const getButtonColor = () => {
    return positiveWindowDesign?.buttonsColor;
  };

  // Helper function to parse additional platforms
  const getAdditionalPlatforms = () => {
    
    if (!positiveWindowDesign?.additionalPlatforms) {
      return { google: true, yelp: true }; // Default to both platforms
    }
    
    try {
      const parsed = JSON.parse(positiveWindowDesign.additionalPlatforms);
      return parsed;
    } catch (error) {
      return { google: true, yelp: true };
    }
  };

  // Show loading state while fetching design
  if (isLoadingDesign) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-baseBlue mx-auto mb-4"></div>
          <p className="text-base-coolGray">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center flex-col min-h-screen py-6 gap-6">
      <div className="bg-white text-center space-y-8 rounded max-w-[845px] w-full py-6 px-20 flex justify-center items-center flex-col mt-auto">
        <div className="flex items-center justify-center gap-2">
          <div
            className="w-12 h-12 flex items-center justify-center  rounded-lg shrink-0"
            style={{ color: `#424244`, background: `#42424414` }}
          >
            <span className="font-bold text-lg">{businessData?.name?.charAt(0) || reviewData?.businessName?.charAt(0) || 'D'}</span>
          </div>
          <div className="flex flex-col">
            <h5 className="font-bold mr-auto break-all text-lg leading-none">
              {businessData?.name || reviewData?.businessName || 'DevX'}
            </h5>
          </div>
        </div>
        <h4 className="font-extrabold text-4xl">
          {positiveWindowDesign?.heading || 'Thank you for your review'}
        </h4>
        <p className="text-base-coolGray text-lg">
          {positiveWindowDesign?.description || 'We make it easy for your to share your exact review on our other platforms, just click the buttons below, it helps us out tremendously!'}
        </p>
        
        {/* Image section */}
        <div className="w-fit">
          {positiveWindowDesign?.imagePath && positiveWindowDesign.imagePath !== null ? (
            <img 
              src={positiveWindowDesign.imagePath} 
              alt="Thank you illustration"
              className="max-w-full h-auto"
            />
          ) : (
            <GoodReviewIllustration />
          )}
        </div>
        <hr className="border-t border[#E7EAF4] my-4 w-full" />
        
        {/* Cross-platform review buttons */}
        {(() => {  
          const shouldShow = (positiveWindowDesign?.additionalPlatforms && (yelpUrl || googleUrl)) || 
                            (!positiveWindowDesign && (yelpUrl || googleUrl));
          
          return shouldShow;
        })() && (
          <div className="w-full space-y-4">
            <div className="text-left w-full">
              <h5 className="font-bold text-lg">
                {positiveWindowDesign?.positivePlatformTitle || 'Leave the same review on another platforms'}
              </h5>
              <p className="text-base-coolGray">
              {positiveWindowDesign?.positiveDescription || `You don't need to re-enter anything`}
             
              </p>
            </div>
            <div className="space-y-2 w-full">
              {(() => {
                // If there's a positiveWindowDesign, use its additionalPlatforms logic
                if (positiveWindowDesign?.additionalPlatforms) {
                  const additionalPlatforms = getAdditionalPlatforms();
                  
                  const enabledPlatforms = [];
                  
                  // Check which platforms are enabled in additionalPlatforms and have URLs
                  if (additionalPlatforms.google && googleUrl) {
                    enabledPlatforms.push({ platform: 'google', url: googleUrl });
                  }
                  if (additionalPlatforms.yelp && yelpUrl) {
                    enabledPlatforms.push({ platform: 'yelp', url: yelpUrl });
                  }
                  
                  if (enabledPlatforms.length === 0) {
                    return null;
                  }
                  
                  return enabledPlatforms.map((platformData) => (
                    <button
                      key={platformData.platform}
                      type="button"
                      onClick={() => handlePlatformClick(platformData.platform as 'yelp' | 'google')}
                      className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                      style={{ 
                        minWidth: 220,
                        backgroundColor: getButtonColor() || (platformData.platform === 'yelp' ? '#DC0000' : '#4285F4')
                      }}
                    >
                      <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                        {platformData.platform === 'yelp' ? 
                          <YelpIcon style={{ width: 20, height: 20 }} /> : 
                          <GoogleIcon style={{ width: 20, height: 20 }} />
                        }
                      </div>
                      <span className="text-center">Leave the same review on {platformData.platform === 'yelp' ? 'Yelp' : 'Google'}</span>
                    </button>
                  ));
                } else {
                  // Fallback logic: Show Yelp first if available, then Google
                  const fallbackPlatforms = [];
                  
                  if (yelpUrl) {
                    fallbackPlatforms.push({ platform: 'yelp', url: yelpUrl });
                  } else if (googleUrl) {
                    fallbackPlatforms.push({ platform: 'google', url: googleUrl });
                  }
                  
                  if (fallbackPlatforms.length === 0) {
                    return null;
                  }
                  
                  return fallbackPlatforms.map((platformData) => (
                    <button
                      key={platformData.platform}
                      type="button"
                      onClick={() => handlePlatformClick(platformData.platform as 'yelp' | 'google')}
                      className="w-full relative flex items-center justify-center py-3 px-4 rounded-lg hover:bg-brand-baseBlue/90 transition-colors duration-200 text-white font-medium"
                      style={{ 
                        minWidth: 220,
                        backgroundColor: platformData.platform === 'yelp' ? '#DC0000' : '#4285F4'
                      }}
                    >
                      <div className="absolute left-4 bg-white rounded-md p-1.5 flex items-center justify-center w-10 h-10">
                        {platformData.platform === 'yelp' ? 
                          <YelpIcon style={{ width: 20, height: 20 }} /> : 
                          <GoogleIcon style={{ width: 20, height: 20 }} />
                        }
                      </div>
                      <span className="text-center">Leave the same review on {platformData.platform === 'yelp' ? 'Yelp' : 'Google'}</span>
                    </button>
                  ));
                }
              })()}
            </div>
          </div>
        )}

        <hr className="border-base-coolGray" />
      </div>

      <p className="mt-auto text-base-coolGray">
        Powered by
        <Link to="/sign-up" className="ml-1 text-brand-baseBlue font-bold">
          Echo Ratings
        </Link>
      </p>
    </div>
  );
};
